import { NoteTask } from "creso-core/types";
import { format } from "date-fns";
import { Trash2Icon } from "lucide-react";
import { Button, Checkbox, cn } from "web-design-system";
import { useDeleteEvent, useUpdateEvent } from "creso-core/hooks";
import { handleError } from "@/utils/error";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { TaskEditor } from "./task-editor";

export function NoteTaskItem(props: {
  task: NoteTask;
  isEditing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  subText?: React.ReactNode;
}) {
  const { task, isEditing, onEditStart, onEditEnd } = props;
  const { mutateAsync: deleteTask, isPending: isDeleting } = useDeleteEvent(
    task.note_id
  );
  const { mutateAsync: updateTask, isPending: isUpdating } = useUpdateEvent();

  const subText = props.subText || (
    <span className="text-xs font-medium text-muted-foreground">
      {task.date ? format(task.date, "PPP") : "No due date set."}
    </span>
  );

  return (
    <div className="group w-full gap-2 flex flex-row items-start">
      <Checkbox
        id={task.id?.toString()}
        className="mt-0.5 shrink-0"
        checked={task.is_deprecated}
        disabled={isUpdating}
        onCheckedChange={(checked) => {
          updateTask({ id: task.id, is_deprecated: !!checked }).catch((e) =>
            handleError(e, "Failed to update task")
          );
        }}
      />
      <ConditionalRenderer condition={!isEditing}>
        <div className="flex flex-row group border rounded-lg border-transparent flex-1">
          <div
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();
              onEditStart();
            }}
            className="flex rounded-lg hover:bg-card -my-3 p-3 flex-col gap-2 flex-1 min-w-0"
          >
            <span
              className={cn(
                "text-sm",
                task.is_deprecated && "line-through text-muted-foreground"
              )}
            >
              {task.note}
            </span>
            {subText}
          </div>
          <Button
            disabled={isDeleting}
            onClick={(e) => {
              e.stopPropagation();
              deleteTask(task.id).catch((e) =>
                handleError(e, "Failed to delete task")
              );
            }}
            className="shrink-0 ml-auto transition-opacity duration-100 opacity-0 group-hover:opacity-100"
            variant="ghost"
            size="icon"
          >
            <Trash2Icon />
          </Button>
        </div>
      </ConditionalRenderer>
      <ConditionalRenderer condition={isEditing}>
        <TaskEditor className="flex-1 -my-3" task={task} onFinish={onEditEnd} />
      </ConditionalRenderer>
    </div>
  );
}
